/**
 * Drochka (Cards Trainer) API — typed client for /v3/drochka-exercise* endpoints.
 */
import apiClient from './client';

export interface DrochkaExercise {
  id: string;
  name?: string;
  description?: string;
  studentTask?: string;
  avatar?: string;
  imageUrl?: string;
  videoUrl?: string;
  copyrightText?: string;
  ownerId?: string;
  access?: string;
  items?: DrochkaCard[];
  /** Number of cards — present in list responses (items array is stripped for performance) */
  itemsCount?: number;
  timestamp?: number;
  updatedTimestamp?: number;
  [key: string]: unknown;
}

export interface DrochkaCard {
  id?: string;
  text?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  time?: number;
  [key: string]: unknown;
}

const DrochkaAPI = {
  /** Get all drochka exercises */
  async getAll(): Promise<DrochkaExercise[]> {
    const { data } = await apiClient.get<any>('/v3/drochka-exercises/all');
    const list = (data as any)?.result ?? data ?? [];
    return Array.isArray(list) ? list : [];
  },

  /** Get public drochka exercises for student browse */
  async getPublicDrochka(): Promise<DrochkaExercise[]> {
    try {
      const { data } = await apiClient.get<any>('/v3/drochka-exercises/all', { params: { access: 'public' } });
      const list = (data as any)?.result ?? data ?? [];
      const all = Array.isArray(list) ? list : [];
      return all.filter((f: any) => f.access === 'public' || f.access === 'shared' || !f.access);
    } catch {
      return [];
    }
  },

  /** Get a single drochka exercise by ID */
  async getById(id: string): Promise<DrochkaExercise> {
    const { data } = await apiClient.get<any>(`/v3/drochka-exercise/${id}`);
    return (data as any)?.result ?? data;
  },

  /** Create a new drochka exercise */
  async create(payload: Partial<DrochkaExercise>): Promise<DrochkaExercise> {
    const { data } = await apiClient.post<DrochkaExercise>(
      '/v3/drochka-exercise',
      payload,
    );
    return data;
  },

  /** Update a drochka exercise */
  async update(payload: Partial<DrochkaExercise> & { id: string }): Promise<DrochkaExercise> {
    const { data } = await apiClient.put<DrochkaExercise>(
      `/v3/drochka-exercise/${payload.id}`,
      payload,
    );
    return data;
  },

  /** Clone a drochka exercise — creates a copy with '(копия)' appended to the name */
  async clone(id: string): Promise<DrochkaExercise> {
    const original = await this.getById(id);
    const { id: _id, timestamp: _ts, updatedTimestamp: _uts, ...rest } = original as any;
    return this.create({ ...rest, name: `${original.name ?? ''} (копия)` });
  },

  /** Delete a drochka exercise (moves to trash) */
  async deleteDrochka(id: string): Promise<void> {
    await apiClient.delete(`/v3/drochka/${id}`);
  },

  /** Get drochka exercises in trash */
  async getTrash(): Promise<DrochkaExercise[]> {
    const { data } = await apiClient.get<DrochkaExercise[]>('/v3/drochka/trash');
    return data ?? [];
  },

  /** Restore a drochka exercise from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/v3/drochka/${id}/restore`, {});
  },

  /** Permanently delete a drochka exercise */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/v3/drochka/${id}/permanent`);
  },
};

export default DrochkaAPI;
